import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle, Download, Info } from 'lucide-react'
import { downloadCertZip } from '@/services/zip'
import type { IssueDeviceResult } from '@/types/device'

interface CertDownloadDialogProps {
  result: IssueDeviceResult | null
  onClose: () => void
}

export default function CertDownloadDialog({
  result,
  onClose,
}: CertDownloadDialogProps) {
  const { t } = useTranslation()
  const [downloaded, setDownloaded] = useState(false)

  useEffect(() => {
    if (result) setDownloaded(false)
  }, [result])

  if (!result) return null

  // 秘密鍵が SSM に保管済みなら機材詳細から再ダウンロードできる
  const keyStored = Boolean(result.privateKeyStored)

  const handleDownload = async () => {
    await downloadCertZip(result)
    setDownloaded(true)
  }

  // 鍵が保管されていない場合、閉じると秘密鍵は破棄される。未ダウンロード時は確認を挟む。
  const handleClose = () => {
    if (
      !downloaded &&
      !keyStored &&
      !window.confirm(t('downloadDialog.confirmClose'))
    ) {
      return
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center gap-2 px-6 py-4 border-b">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            {t('downloadDialog.title')}
          </h2>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="text-sm text-gray-700 space-y-1">
            <p>
              <span className="font-medium">{t('devices.deviceName')}:</span>{' '}
              {result.deviceName}
            </p>
            <p>
              <span className="font-medium">{t('devices.siteId')}:</span>{' '}
              {result.siteId}
            </p>
          </div>

          {keyStored ? (
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{t('downloadDialog.storedNote')}</span>
            </div>
          ) : (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{t('downloadDialog.warning')}</span>
            </div>
          )}

          <button
            onClick={handleDownload}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
          >
            {downloaded ? (
              <>
                <CheckCircle className="h-4 w-4" />
                {t('downloadDialog.downloaded')}
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                {t('downloadDialog.download')}
              </>
            )}
          </button>

          <p className="text-xs text-gray-500">
            {t('downloadDialog.contents')}
          </p>
        </div>

        <div className="flex justify-end px-6 py-4 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  )
}
